if (typeof window !== 'undefined' && !window.dataLayer) {
  window.dataLayer = [];
}

/** A product as the data layer's `items` list wants it. */
interface EcommerceItem {
  price?: number;
  currency?: string;
  item_list_id?: string;
  item_list_name?: string;
  [key: string]: unknown;
}

interface GaEcommerceTrackingOptions {
  name: string;
  type: string;
  rootMargin: string;
  threshold: number | number[];
  once: boolean;
  limit: number;
  /** Selector for the button that counts as a click inside the node. */
  button?: string;
  data: EcommerceItem;
}

let views: EcommerceItem[] = [];

function cleanData(data: Record<string, unknown> = {}) {
  const cleaned: EcommerceItem = {};

  Object.keys(data).forEach(key => {
    if (data[key]) {
      if (key === 'price') {
        cleaned[key] = parseFloat(data[key] as string);
      } else {
        cleaned[key] = data[key];
      }
    }
  });

  return cleaned;
}

export class GaEcommerceTracking {
  props: GaEcommerceTrackingOptions;
  node: HTMLElement;
  timeout: ReturnType<typeof setTimeout> | undefined;
  interval: ReturnType<typeof setInterval> | undefined;
  button?: HTMLElement | null;
  observer?: IntersectionObserver | null;

  constructor(
    node: HTMLElement,
    options: Partial<Omit<GaEcommerceTrackingOptions, 'data'>> & {
      data?: Record<string, unknown>;
    } = {}
  ) {
    this.props = {
      name: 'GaEcommerceTracking',
      type: 'view',
      rootMargin: '0px 0px 0px 0px',
      threshold: 0.5,
      once: false,
      limit: 10,
      ...options,
      data: cleanData(options.data),
    };
    this.node = node;

    /* prevent double decoration */
    if (this.node.decorator) {
      if (this.node.decorator instanceof GaEcommerceTracking) {
        this.node.decorator.destroy();
      }
    }

    this.node.decorator = this;
    this.timeout = undefined;
    this.interval = undefined;

    if (this.props.type === 'view') {
      this.node.addEventListener('ready', this.initializeView);

      this.timeout = setTimeout(this.initializeView, 1000);
    } else {
      this.initializeSelect();
    }
  }

  initializeView = () => {
    this.destroy();
    this.timeout = setTimeout(() => {
      this.button = this.props.button
        ? this.node.querySelector<HTMLElement>(this.props.button)
        : null;

      if (this.button) {
        this.button.addEventListener('click', this.trackClick, { once: true });
      }

      this.registerObserver();

      this.interval = setInterval(this.processViewItems, 5000);
    }, 1000);
  };

  initializeSelect = () => {
    this.node.addEventListener('click', this.trackClick, { once: true });
  };

  destroy = () => {
    this.node.removeEventListener('click', this.trackClick);

    clearTimeout(this.timeout);
    clearInterval(this.interval);

    if (this.button) {
      this.button.removeEventListener('click', this.trackClick);
    }

    if (this.observer) {
      this.observer.unobserve(this.node);
    }

    this.observer = null;
    this.node.decorator = null;
  };

  registerObserver = () => {
    const { rootMargin, threshold } = this.props;
    this.observer = new IntersectionObserver(this.onIntersection, {
      rootMargin,
      threshold,
    });
    this.observer.observe(this.node);
  };

  onIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(({ intersectionRatio }) => {
      if (intersectionRatio > 0) {
        if (this.props.type === 'view') {
          this.trackView();
          if (this.props.once) {
            this.observer!.unobserve(this.node);
          }
        }
      }
    });
  };

  processViewItems = () => {
    if (typeof window !== 'undefined' && window.dataLayer && views.length) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          items: views.map((item, index) => ({ ...item, index: index + 1 })),
        },
      });
      views = [];
    }
  };

  trackView = () => {
    const { data, limit } = this.props;
    const exists = views.find(
      item => JSON.stringify(item) == JSON.stringify(data)
    );

    if (!exists) {
      views.push(data);
      if (views.length >= limit) {
        this.processViewItems();
      }
    }
  };

  trackClick = (e: MouseEvent) => {
    e.preventDefault();
    const { data } = this.props;

    this.processViewItems();
    window.dataLayer!.push({ ecommerce: null });
    window.dataLayer!.push({
      event: 'select_item',
      ecommerce: {
        currency: data.currency || 'NOK',
        value: data.price,
        item_list_id: data.item_list_id,
        item_list_name: data.item_list_name,
        items: [data],
      },
    });
    this.node.removeEventListener('click', this.trackClick);

    if (this.button) {
      this.button.removeEventListener('click', this.trackClick);
    }

    (e.target as HTMLElement).click();
  };
}

export default (
  node: HTMLElement,
  props?: ConstructorParameters<typeof GaEcommerceTracking>[1]
) => new GaEcommerceTracking(node, props);
